import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, XIcon, ShieldCheckIcon, AlertTriangleIcon, CheckCircle2Icon, ChevronDownIcon } from 'lucide-react';
function VimeoPlayer({
  isFloating,
  onDismiss
}: {
  isFloating: boolean;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  return <div className={`relative z-10 w-full rounded-lg shadow-2xl overflow-hidden transition-all duration-500 ease-in-out ${isFloating ? 'fixed bottom-4 right-4 !w-[300px] md:!w-[380px] z-[90] rounded-xl border border-gold/30' : 'border border-gold/20'}`} style={isFloating ? {
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
  } : {}} data-id="element-241">
      <div style={{
      padding: '56.25% 0 0 0',
      position: 'relative'
    }} data-id="element-242">
        <iframe src="https://player.vimeo.com/video/1177040592?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} title="AI Web Closer VSL" data-id="element-243" />
      </div>
      <AnimatePresence data-id="element-244">
        {isFloating && <motion.button initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.5
      }} transition={{
        duration: 0.2
      }} onClick={onDismiss} className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 transition-colors" aria-label="Cerrar video flotante" data-id="element-245">
            <XIcon className="h-3.5 w-3.5 text-white" data-id="element-246" />
          </motion.button>}
      </AnimatePresence>
    </div>;
}
export function HeroLP5() {
  const [isFloating, setIsFloating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const videoSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = videoSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        setIsFloating(true);
      } else {
        setIsFloating(false);
        setIsDismissed(false);
      }
    }, {
      threshold: 0.1
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  const handleDismiss = () => {
    setIsDismissed(true);
  };
  const handleCTAClick = () => {
    if ((window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'AI Web Closer Course',
        content_type: 'product',
        value: 27,
        currency: 'USD'
      });
    }
  };
  return <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden px-4 pt-12 pb-20" data-id="element-247">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy-dark" data-id="element-248">
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-0 right-0 w-96 h-96 bg-gold/20 rounded-full blur-3xl" data-id="element-249" />
        <motion.div animate={{
        scale: [1.2, 1, 1.2],
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-0 left-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" data-id="element-250" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto w-full" data-id="element-251">
        {/* Headlines */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="text-center mb-10 md:mb-14" data-id="element-252">
          <p className="font-body text-base md:text-lg text-gray-300 font-medium mb-4" data-id="element-253">
            Si Buscas Crear Un Negocio Digital Rentable...
          </p>

          <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 max-w-5xl mx-auto" data-id="element-254">
            Usa Este <span className="text-gold" data-id="element-255">Ecosistema Con IA</span> Para
            Crear Y Vender Sitios Web En Menos De 7 Días...{' '}
            <span className="text-gold" data-id="element-256">Incluso Si Empiezas Desde Cero.</span>
          </h1>

          <p className="font-body text-lg md:text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-257">
            El mismo Ecosistema que ha generado miles de dólares para nuestros
            miembros, sin saber programar y sin experiencia previa.
          </p>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12 items-start" data-id="element-258">
          {/* Left Column: Video */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="lg:col-span-3 relative" data-id="element-259">
            <div ref={videoSentinelRef} className="absolute inset-0 pointer-events-none" data-id="element-260" />
            <VimeoPlayer isFloating={!isDismissed && isFloating} onDismiss={handleDismiss} data-id="element-261" />
          </motion.div>

          {/* Right Column: Offer Card */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="lg:col-span-2" data-id="element-262">
            <div className="bg-navy-dark/80 backdrop-blur-sm border border-gold/40 rounded-2xl p-6 relative overflow-hidden flex flex-col items-center text-center shadow-2xl shadow-black/50" data-id="element-263">
              {/* Urgency Badge */}
              <div className="absolute top-0 inset-x-0 bg-red-900/40 border-b border-red-500/30 py-1.5 flex items-center justify-center gap-2" data-id="element-264">
                <AlertTriangleIcon className="w-4 h-4 text-red-400" data-id="element-265" />
                <span className="text-red-400 text-xs font-bold uppercase tracking-wider" data-id="element-266">
                  Descuento por tiempo limitado
                </span>
              </div>

              <h3 className="text-gold font-heading font-black text-2xl md:text-3xl mt-8 mb-4 uppercase leading-tight" data-id="element-267">
                Obtén Acceso Instantáneo Hoy
              </h3>

              <ChevronDownIcon className="w-8 h-8 text-gold mb-6 animate-bounce" data-id="element-268" />

              {/* Product Details Area */}
              <div className="w-full bg-navy/50 border border-gold/20 rounded-xl p-5 mb-6 space-y-3 text-left" data-id="element-269">
                <div className="flex items-start gap-3" data-id="element-270">
                  <CheckCircle2Icon className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-271" />
                  <span className="text-sm md:text-base text-gray-200 font-medium" data-id="element-272">
                    Software AI Web Builder
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-273">
                  <CheckCircle2Icon className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-274" />
                  <span className="text-sm md:text-base text-gray-200 font-medium" data-id="element-275">
                    Local Scout (Buscador de Clientes)
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-276">
                  <CheckCircle2Icon className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-277" />
                  <span className="text-sm md:text-base text-gray-200 font-medium" data-id="element-278">
                    Guiones de Venta Probados
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-279">
                  <CheckCircle2Icon className="w-5 h-5 text-gold shrink-0 mt-0.5" data-id="element-280" />
                  <span className="text-sm md:text-base text-gray-200 font-medium" data-id="element-281">
                    Plantillas de Sitios Web Premium
                  </span>
                </div>
              </div>

              {/* Value Breakdown */}
              <div className="w-full border-t border-gold/20 pt-5 mb-2" data-id="element-282">
                <p className="text-gray-400 text-lg font-medium mb-1" data-id="element-283">
                  Valor Total:{' '}
                  <span className="line-through decoration-red-500/50" data-id="element-284">
                    $6,364+
                  </span>
                </p>
                <p className="text-gray-300 text-lg font-medium mb-2" data-id="element-285">
                  Ahorro: <span className="text-red-400 font-bold" data-id="element-286">$6,337</span>
                </p>
                <div className="bg-gold/10 border border-gold/30 rounded-lg py-3 px-4 inline-block w-full" data-id="element-287">
                  <p className="text-gold font-black text-3xl md:text-4xl" data-id="element-288">
                    Hoy: Solo $27
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Massive CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.8
      }} className="max-w-4xl mx-auto text-center" data-id="element-289">
          <a href="/checkout-2" onClick={handleCTAClick} className="group block w-full bg-gradient-to-b from-gold-light to-gold hover:from-gold hover:to-gold-dark text-navy rounded-2xl shadow-[0_0_40px_rgba(212,175,55,0.3)] hover:shadow-[0_0_60px_rgba(212,175,55,0.5)] transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] p-4 md:p-6 border-b-4 border-navy/20" data-id="element-290">
            <div className="flex flex-col items-center justify-center" data-id="element-291">
              <div className="font-heading font-black text-2xl md:text-4xl lg:text-5xl mb-2 uppercase tracking-tight" data-id="element-292">
                SÍ, ¡QUIERO ACCESO AHORA!
              </div>
              <div className="font-body font-bold text-sm md:text-lg lg:text-xl opacity-90 uppercase tracking-wide flex items-center gap-2" data-id="element-293">
                Obtén Acceso Inmediato Al Ecosistema AI Web Closer
                <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-2 transition-transform" data-id="element-294" />
              </div>
            </div>
          </a>

          <div className="flex items-center justify-center gap-2 mt-6 text-gray-400 font-body text-sm md:text-base" data-id="element-295">
            <ShieldCheckIcon className="h-5 w-5 text-gold" data-id="element-296" />
            <span data-id="element-297">Garantía de 30 Días Sin Preguntas. Sistema Probado.</span>
          </div>
        </motion.div>
      </div>
    </section>;
}